// utils/simulationRule.ts

import { PlayerStat, ProgressionRule, SimulationResult } from "../types/tournament";

export const runSimulation = (
  numberOfGroups: number,
  numberOfPlayers: number,
  rules: ProgressionRule[]
): SimulationResult => {
  const logs: string[] = [];


  // Group distribution
  const baseSize = Math.floor(numberOfPlayers / numberOfGroups);
  const remainder = numberOfPlayers % numberOfGroups;

  // Mock standings
  let userIdCounter = 1;
  const mockStats: PlayerStat[] = [];
  const mockSourceGroups: Record<number, PlayerStat[]> = {};

  for (let g = 0; g < numberOfGroups; g++) {
    const groupId = g;
    mockSourceGroups[groupId] = [];
    const currentGroupSize = g < remainder ? baseSize + 1 : baseSize;

    for (let p = 0; p < currentGroupSize; p++) {
      const rank = p + 1;
      // Win calculation
      const wins = currentGroupSize - p;
      const stat: PlayerStat = {
        userId: userIdCounter++,
        groupId: groupId,
        rank: rank,
        isAllocated: false,
      };

      // Attach stats for sorting if needed
      // Same-rank players from different boxes tie on wins; a small per-box
      // variation (always < 10, so within-box order still follows wins) keeps
      // "Stats" and "Box order" from looking identical in the preview.
      (stat as any).wins = wins;
      (stat as any).pointsDiff = wins * 10 + ((g * 5 + p * 3) % 7);

      mockStats.push(stat);
      mockSourceGroups[groupId].push(stat);

    }
  }

  const idMap = new Map<number, number>();
  for (let i = 1; i <= numberOfGroups; i++) {
    idMap.set(i, (i - 1));
  }


  // Allocation
  const allocatedUserIds = new Set<number>();
  const results: { groupName: string; players: PlayerStat[] }[] = [];

  for (const finalBoxRule of rules) {
    const selectedForBox: PlayerStat[] = [];

    for (const sourceRule of finalBoxRule.sources) {
      const targetMockGroupIds = sourceRule.sourceGroupIds.map((id) => idMap.get(Number(id)) ?? -1);

      let candidates = mockStats.filter((stat) => {
        const groupMatch = targetMockGroupIds.includes(stat.groupId);
        const posMatch = sourceRule.positions.includes(stat.rank);
        const notAllocated = !allocatedUserIds.has(stat.userId);
        return groupMatch && posMatch && notAllocated;
      });

      // Sort like the backend: finishing position first. Within a position,
      // 'position' (Box order) = lower box first; otherwise (stats / unset) best stats.
      candidates.sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (sourceRule.sortBy === 'position') return a.groupId - b.groupId;
        const sA = a as any;
        const sB = b as any;
        if (sB.wins !== sA.wins) return sB.wins - sA.wins;
        return sB.pointsDiff - sA.pointsDiff;
      });

      // Limit
      if (sourceRule.limit && sourceRule.limit > 0) {
        candidates = candidates.slice(0, sourceRule.limit);
      }

      candidates.forEach((c) => {
        allocatedUserIds.add(c.userId);
        c.isAllocated = true;
        c.destinationGroup = finalBoxRule.targetGroupName;
        selectedForBox.push(c);
      });
    }

    results.push({
      groupName: finalBoxRule.targetGroupName,
      players: selectedForBox,
    });
  }


  return { mockStandings: mockSourceGroups, targetGroups: results, logs };
};
